/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MatDialogModule } from '@angular/material/dialog';
import { FlexLayoutModule } from '@angular/flex-layout';

import { CrimsonModule, importTranslateModuleForRoot, DeviceOSType } from 'crimson';
import { NetworkHttpEndpointRetryConfig, NvHttpEndpointDefaultRetryConfig } from 'nv-angular-http-endpoint';
import { AppConfigLoader, AppConfigParameters } from 'app-config';
import { Configuration } from './shared/configuration/Configuration';
import { StarfleetConfig, StarfleetModule } from 'starfleet';
import { environment } from '../environments/environment';
import { LoginComponent } from './login/login.component'; //playtest-live login component
import { PlaytestLiveConfigProvider } from './shared/configuration/configuration.provider';
import { ProjectInterfaceConfig } from 'project-interface';
import { Plugins } from 'plugins-notification';
import { GamepadServiceProvider, NvGamepadConfig } from 'gamepad';
import { NvHigConfig, NV_HIG_CONFIG } from 'hig';
import { JsEventsDefaultConfig, JsEventsModule, JsEventsServerConfig } from 'js-events';
import { SharedStorageModule } from 'shared-storage';
import { AppBarModule } from 'app-bar';
import { CommonBrowserPluginsProvider } from '../../../../libraries/common-browser-plugins/src/lib/plugins.provider';
import { SystemInfoPluginConfig } from '../../../../libraries/common-browser-plugins/src/lib/systeminfo/systeminfo.config';

import { PlaytestHomeComponent } from './playtest-home/playtest-home.component';
import { PlaytestLaunchComponent } from './playtest-launch/playtest-launch.component';
import { SessionsComponent } from './sessions/sessions.component';
import { SettingsComponent } from './settings/settings.component';
import { CommonFooterComponent } from './common-footer/common-footer.component';
import { InvitationResultComponent } from './invitation-result/invitation-result.component';
import { MaterialDesignModule } from './material-design.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

export function loadStarfleetConfig(appConfig: Configuration): StarfleetConfig {
    return appConfig.starfleet;
}

export function loadJsEventsConfig(appConfig: Configuration): JsEventsServerConfig {
    return appConfig.jsEvents || JsEventsDefaultConfig;
}

function loadSystemInfoPluginConfig(): SystemInfoPluginConfig {
    // On certain Web platforms, there are platform APIs for obtaining system information
    // which isn't available via standard Web APIs.
    // To use such information in the web app, extra system information is first obtained from the native app,
    // and then passed to the web app via query parameters when launching it.
    const config: SystemInfoPluginConfig = {
        systemInfoOverrideConfig: {
            allowedOSs: [DeviceOSType.WebOS, DeviceOSType.Tizen],
            override: {}
        }
    };
    const queryParams = new URLSearchParams(window.location.search);
    const propertyNamesByQueryKey = new Map([
        ['modelName', 'SystemName'],
        ['osVersion', 'OSVersion']
    ]);
    propertyNamesByQueryKey.forEach((propertyName, queryKey, _) => {
        const value = queryParams.get(queryKey);
        if (value) {
            config.systemInfoOverrideConfig.override[propertyName] = value;
        }
    });
    return config;
}

export function loadProjectInterfaceConfig(): ProjectInterfaceConfig {
    return {
        waitForDialogClose: false
    };
}

export function loadHigConfig(): NvHigConfig {
    return {
        tvMode: false,
        offDeviceLinkConfig: {
            title: 'offDeviceLink.title',
            primaryButtonText: 'offDeviceLink.button'
        }
    };
}

function loadGamepadConfig(): NvGamepadConfig {
    const gamepadConfig: NvGamepadConfig = {
        implementationType: 'no-op'
    };
    return gamepadConfig;
}

export function loadNetworkHttpEndpointRetryConfig(appConfig: Configuration): NetworkHttpEndpointRetryConfig {
    return {
        ...NvHttpEndpointDefaultRetryConfig,
        ...appConfig.networkHttpEndpointRetryConfig
    };
}

const BrowserPluginsProvider: Provider[] = [...CommonBrowserPluginsProvider];

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        StarfleetModule,
        importTranslateModuleForRoot(),
        CrimsonModule.configure(environment),
        BrowserModule,
        SharedStorageModule,
        AppBarModule,
        FlexLayoutModule,
        MaterialDesignModule,
        MatDialogModule,
        AppRoutingModule,
        JsEventsModule,
        BrowserAnimationsModule,
        InvitationResultComponent,
        LoginComponent,
        PlaytestHomeComponent,
        PlaytestLaunchComponent,
        SessionsComponent,
        CommonFooterComponent,
        SettingsComponent
    ],
    providers: [
        BrowserPluginsProvider,
        {
            provide: AppConfigParameters,
            useValue: { filePath: environment.configFileName, production: environment.production }
        },
        {
            provide: NetworkHttpEndpointRetryConfig,
            useFactory: loadNetworkHttpEndpointRetryConfig,
            deps: [Configuration]
        },
        {
            provide: StarfleetConfig,
            useFactory: loadStarfleetConfig,
            deps: [Configuration]
        },
        AppConfigLoader,
        PlaytestLiveConfigProvider,
        {
            provide: SystemInfoPluginConfig,
            useFactory: loadSystemInfoPluginConfig
        },
        {
            provide: JsEventsServerConfig,
            useFactory: loadJsEventsConfig,
            deps: [Configuration]
        },
        {
            provide: ProjectInterfaceConfig,
            useFactory: loadProjectInterfaceConfig
        },
        {
            provide: NvGamepadConfig,
            useFactory: loadGamepadConfig
        },
        {
            provide: NV_HIG_CONFIG,
            useFactory: loadHigConfig
        },
        GamepadServiceProvider,
        {
            provide: Plugins,
            useValue: {
                mandatoryModuleNames: [],
                optionalModulesNames: []
            }
        },
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class AppModule {}
